// Plugin diagnostyczny — mierzy rejestry oparte o import.meta.glob (parytet bundlerów rollup/rolldown).
// Domyślnie martwy: bez flagi `?registryDump=1` nie robi nic (żadnych żądań, logów, window).
// Wszystkie globy są lazy (bez eager), więc bez flagi mapy nie wykonują importów — zero wpływu na runtime.
// Zawartość eager-owych rejestrów (tłumaczenia, sitemap, definicje form) pobieramy dopiero pod flagą,
// wykonując importery ręcznie — dlatego duplikat globów tutaj nie ściąga tych modułów przy normalnym starcie.

// Bazy `../../../../` MUSZĄ być identyczne z konsumentami (cms.ts, sitemap.ts) — ten plik leży w
// layers/core/app/plugins/, tak samo jak sitemap.ts, więc baza wspina się do roota Admina i klucze
// są znak w znak takie same jak w rejestrach produkcyjnych.
const cmsModuleSettings = import.meta.glob('./**/_partials/module/*Settings.vue', { base: '../../../../' });
const cmsWidgetSettings = import.meta.glob('./**/app/components/widget/**/*Settings.vue', { base: '../../../../' });
const i18nLocales = import.meta.glob('./**/locales/*/*.ts', { base: '../../../../' });
const sitemapDefs = import.meta.glob('./**/shared/sitemap.ts', { base: '../../../../' });
const formComponents = import.meta.glob('./**/components/forms/blueprints/**/Blueprint.vue', { base: '../../../../' });
const formFilters = import.meta.glob('./**/components/forms/blueprints/**/Filter.vue', { base: '../../../../' });
const formPresenters = import.meta.glob('./**/components/forms/blueprints/**/Presenter.vue', { base: '../../../../' });
const formDefinitions = import.meta.glob('./**/components/forms/blueprints/**/index.ts', { base: '../../../../' });
const formMetas = import.meta.glob('./**/components/forms/blueprints/**/meta.ts', { base: '../../../../' });
const moduleTemplates = import.meta.glob('./**/components/modules/templates/**/Template.vue', { base: '../../../../' });
const moduleSetups = import.meta.glob('./**/components/modules/templates/**/Setup.vue', { base: '../../../../' });
const automationUnits = import.meta.glob('./**/components/automations/units/**/*.vue', { base: '../../../../' });
const automationConnections = import.meta.glob('./**/components/automations/connections/**/*.vue', { base: '../../../../' });
const dashboardWidgets = import.meta.glob('./**/components/dashboard/**/Widget.vue', { base: '../../../../' });

function rawKeys(map: Record<string, unknown>)
{
    return Object.keys(map).toSorted();
}

function keyCount(map: Record<string, unknown>)
{
    return { rawKeys: rawKeys(map), count: Object.keys(map).length };
}

export default defineNuxtPlugin({
    name: 'zzz-registry-dump',
    // Po cms, żeby store rejestrów modułów/widgetów był już wypełniony przy odczycie.
    dependsOn: ['cms'],
    async setup()
    {
        if (!import.meta.client)
            return;

        const { $config } = useNuxtApp();

        // Aktywacja flagą, domyślnie martwy. W trybie SPA (ssr: false) publiczny runtimeConfig jest
        // zamrożony w buildzie i nie reaguje na NUXT_PUBLIC_* w runtime, dlatego głównym przełącznikiem
        // jest parametr URL `?registryDump=1` czytany po stronie klienta. Zapytanie odczytujemy z
        // pierwotnego adresu wejścia (guard może przekierować na /auth/login i zgubić query).
        const params = new URLSearchParams(window.location.search);
        const active = params.get('registryDump') === '1' || $config.public.registryDump === '1';

        if (!active)
            return;

        const store = useComponentsStore();

        const i18nFiles: Record<string, any> = {};

        for (const [key, importer] of Object.entries(i18nLocales))
        {
            const mod = await importer() as any;
            const translation = mod.default ?? {};
            const messages = translation.Messages ?? {};

            i18nFiles[key] = {
                language: translation.Language ?? null,
                keyCount: Object.keys(messages).length,
                keys: Object.keys(messages).toSorted()
            };
        }

        const sitemapFiles: Record<string, any> = {};

        for (const [key, importer] of Object.entries(sitemapDefs))
        {
            const mod = await importer() as any;
            const def = mod.default;

            sitemapFiles[key] = {
                defaultType: typeof def,
                constructorName: def?.constructor?.name ?? null
            };
        }

        const definitionTypes: string[] = [];

        for (const importer of Object.values(formDefinitions))
        {
            const mod = await importer() as any;

            if (mod.Definition?.type != null)
                definitionTypes.push(mod.Definition.type);
        }

        const dump = {
            meta: {
                flag: 'NUXT_PUBLIC_REGISTRY_DUMP',
                layers: $config.public.layers,
                layerPaths: $config.public.layerPaths.toSorted()
            },
            cms: {
                moduleSettings: keyCount(cmsModuleSettings),
                widgetSettings: keyCount(cmsWidgetSettings),
                store: {
                    modules: store.modules.toSorted(),
                    widgets: store.widgets.toSorted()
                }
            },
            i18n: {
                rawKeys: rawKeys(i18nLocales),
                files: i18nFiles
            },
            sitemap: {
                rawKeys: rawKeys(sitemapDefs),
                files: sitemapFiles
            },
            studioForms: {
                components: keyCount(formComponents),
                filters: keyCount(formFilters),
                presenters: keyCount(formPresenters),
                definitions: { ...keyCount(formDefinitions), definitionTypes: definitionTypes.toSorted() },
                metas: keyCount(formMetas)
            },
            studioTemplates: {
                templates: keyCount(moduleTemplates),
                setups: keyCount(moduleSetups)
            },
            studioAutomations: {
                units: keyCount(automationUnits),
                connections: keyCount(automationConnections)
            },
            dashboards: {
                widgets: keyCount(dashboardWidgets)
            }
        };

        (window as any).__registryDump = dump;

        // Marker w jednej linii — harness wyławia go z konsoli, gdy odczyt window zawiedzie.
        console.log('__REGISTRY_DUMP_BEGIN__' + JSON.stringify(dump) + '__REGISTRY_DUMP_END__');
    }
});
